/**
 * Offline Mutation Queue — Sprint v28 (Feb 2026).
 *
 * Persists user mutations locally (IndexedDB) when the network is down
 * or the backend is cold-starting, and flushes them with backoff when
 * connectivity returns. Never loses user work.
 *
 * DESIGN:
 *   - Single object store `queue` keyed by auto-incrementing id.
 *   - Each entry: { id, url, method, body?, headers?, createdAt,
 *                   idempotencyKey, attempts, lastError? }.
 *   - `enqueue()` writes to IndexedDB immediately.
 *   - `flush()` reads oldest-first, POSTs via fetch, deletes on success.
 *   - `startAutoFlush()` listens to window.online + polls every 30 s.
 *   - Every entry carries an `Idempotency-Key` header so re-plays are
 *     safe (backend already has idempotency infra from Sprint v27).
 *
 * USAGE:
 *   import { offlineQueue } from '../lib/offlineQueue';
 *   offlineQueue.startAutoFlush();
 *   await offlineQueue.enqueue({
 *     url: '/api/workspaces/x/messages',
 *     method: 'POST',
 *     body: { text: 'hola' },
 *   });
 */

const DB_NAME = 'nexus_offline_v28';
const DB_VERSION = 1;
const STORE = 'queue';

export interface QueueEntry {
  id?: number;
  url: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  createdAt: number;
  idempotencyKey: string;
  attempts: number;
  lastError?: string;
}

export type QueueStatus = 'idle' | 'syncing' | 'error' | 'offline';

type Listener = (status: QueueStatus, pending: number) => void;

class OfflineQueue {
  private db: IDBDatabase | null = null;
  private listeners = new Set<Listener>();
  private status: QueueStatus = 'idle';
  private pending = 0;
  private flushing = false;
  private pollHandle: number | null = null;

  private async open(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
        }
      };
      req.onsuccess = () => {
        this.db = req.result;
        resolve(req.result);
      };
      req.onerror = () => reject(req.error);
    });
  }

  private notify(status: QueueStatus): void {
    this.status = status;
    for (const l of this.listeners) {
      try { l(status, this.pending); } catch { /* noop */ }
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Emit current state on subscribe.
    try { listener(this.status, this.pending); } catch { /* noop */ }
    return () => this.listeners.delete(listener);
  }

  private newIdempotencyKey(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `off-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async enqueue(input: {
    url: string;
    method: QueueEntry['method'];
    body?: unknown;
    headers?: Record<string, string>;
    idempotencyKey?: string;
  }): Promise<void> {
    const db = await this.open();
    const entry: QueueEntry = {
      url: input.url,
      method: input.method,
      body: input.body,
      headers: input.headers,
      createdAt: Date.now(),
      idempotencyKey: input.idempotencyKey || this.newIdempotencyKey(),
      attempts: 0,
    };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).add(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    await this.refreshCount();
    this.notify('offline');
  }

  private async listEntries(): Promise<QueueEntry[]> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result as QueueEntry[]);
      req.onerror = () => reject(req.error);
    });
  }

  private async deleteEntry(id: number): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async updateEntry(entry: QueueEntry): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async refreshCount(): Promise<void> {
    const entries = await this.listEntries();
    this.pending = entries.length;
    // Update listeners with current status but new count.
    for (const l of this.listeners) {
      try { l(this.status, this.pending); } catch { /* noop */ }
    }
  }

  async flush(): Promise<{ sent: number; failed: number }> {
    if (this.flushing) return { sent: 0, failed: 0 };
    this.flushing = true;
    this.notify('syncing');
    let sent = 0;
    let failed = 0;
    try {
      const entries = (await this.listEntries()).sort((a, b) => a.createdAt - b.createdAt);
      const token = localStorage.getItem('nexus_token');
      for (const entry of entries) {
        try {
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Idempotency-Key': entry.idempotencyKey,
            ...(entry.headers || {}),
          };
          if (token) headers.Authorization = `Bearer ${token}`;
          const resp = await fetch(entry.url, {
            method: entry.method,
            headers,
            body: entry.body !== undefined ? JSON.stringify(entry.body) : undefined,
          });
          if (resp.ok || resp.status === 409 /* already processed */) {
            if (entry.id !== undefined) await this.deleteEntry(entry.id);
            sent++;
          } else if (resp.status >= 500) {
            // Server-side transient — keep entry, bump attempts.
            entry.attempts += 1;
            entry.lastError = `HTTP ${resp.status}`;
            if (entry.id !== undefined && entry.attempts < 10) {
              await this.updateEntry(entry);
            } else if (entry.id !== undefined) {
              // Drop after 10 failed attempts to prevent buildup.
              await this.deleteEntry(entry.id);
            }
            failed++;
            // Stop this pass — probably backend still cold; retry next tick.
            break;
          } else {
            // 4xx (bad request) → drop, log.
            if (entry.id !== undefined) await this.deleteEntry(entry.id);
            failed++;
          }
        } catch (err) {
          entry.attempts += 1;
          entry.lastError = err instanceof Error ? err.message : String(err);
          if (entry.id !== undefined && entry.attempts < 10) {
            await this.updateEntry(entry);
          } else if (entry.id !== undefined) {
            await this.deleteEntry(entry.id);
          }
          failed++;
          break;
        }
      }
    } finally {
      this.flushing = false;
      await this.refreshCount();
      this.notify(this.pending > 0 ? (failed ? 'error' : 'offline') : 'idle');
    }
    return { sent, failed };
  }

  startAutoFlush(): void {
    if (typeof window === 'undefined') return;
    // Listen to online event
    window.addEventListener('online', () => { void this.flush(); });
    window.addEventListener('offline', () => { this.notify('offline'); });
    // Also poll every 30s in case the online event misfires.
    if (this.pollHandle === null) {
      this.pollHandle = window.setInterval(() => {
        if (navigator.onLine && this.pending > 0) void this.flush();
      }, 30000);
    }
    // Initial flush attempt on load
    if (navigator.onLine) void this.flush();
    void this.refreshCount();
  }

  getStatus(): { status: QueueStatus; pending: number } {
    return { status: this.status, pending: this.pending };
  }
}

export const offlineQueue = new OfflineQueue();
